import { AnimatePresence, motion } from 'framer-motion';

import { cx } from 'utils';
import { ROLE_LABEL } from 'config/roles';
import { useAuth } from 'hooks';
import NavList from './NavList';
import { ChevronLeftIcon } from './icons';

/**
 * Sidebar — fixed, collapsible desktop navigation rail (hidden on mobile, where
 * the drawer takes over). Clean white rail with a soft right hairline. Width
 * animates between full and icon-only via Framer Motion; the active route is
 * highlighted by NavList's sliding pill.
 *
 * @param {Array}    items            Role nav entries.
 * @param {string}   role             Role key (for the workspace label).
 * @param {boolean}  collapsed        Icon-only mode.
 * @param {Function} onToggleCollapse Toggle the collapsed state.
 * @param {'solid'|'soft'} [tone]     Active-item style (see NavList).
 * @param {boolean}  [showUser]       Profile card above the collapse toggle
 *                                    (the student skin, after the mockup).
 */
const EASE = [0.22, 1, 0.36, 1];

export default function Sidebar({ items, role, collapsed, onToggleCollapse, tone = 'solid', showUser = false }) {
  const { user, profile } = useAuth();
  const name = profile?.displayName || user?.email || 'Member';
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 76 : 256 }}
      transition={{ duration: 0.34, ease: EASE }}
      className="sticky top-16 z-20 hidden h-[calc(100vh-4rem)] shrink-0 md:block"
    >
      <div className="flex h-full flex-col border-r border-slate-200 bg-white">
        {/* Workspace label */}
        <div className={cx('flex h-14 items-center border-b border-slate-100 px-4', collapsed && 'justify-center px-0')}>
          <AnimatePresence initial={false} mode="wait">
            {collapsed ? (
              <motion.span
                key="dot"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                transition={{ duration: 0.18 }}
                className="h-2 w-2 rounded-full bg-brand-600"
              />
            ) : (
              <motion.div
                key="label"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-2"
              >
                <span className="h-2 w-2 rounded-full bg-brand-600" />
                <span className="text-2xs font-bold uppercase tracking-[0.18em] text-brand-600/70">
                  {ROLE_LABEL[role] || 'Member'} workspace
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2">
          <NavList items={items} collapsed={collapsed} layoutId="sidebar-pill" tone={tone} />
        </div>

        {showUser && (
          <div className={cx('px-3 pb-1', collapsed && 'flex justify-center')}>
            <div
              className={cx('flex items-center gap-2.5 rounded-2xl bg-slate-100 p-2.5', collapsed && 'bg-transparent p-0')}
              title={collapsed ? name : undefined}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-50 text-sm font-bold text-gold-700">
                {initials}
              </span>
              {!collapsed && (
                <span className="min-w-0 leading-tight">
                  <span className="block truncate text-sm font-semibold text-slate-900">{name}</span>
                  <span className="block text-xs text-slate-500">{ROLE_LABEL[role] || 'Member'}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Collapse toggle */}
        <div className="border-t border-slate-100 p-3">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            className={cx(
              'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500',
              'transition-colors duration-200 ease-premium hover:bg-slate-100 hover:text-slate-900',
              collapsed && 'justify-center'
            )}
          >
            <motion.span animate={{ rotate: collapsed ? 180 : 0 }} transition={{ duration: 0.34, ease: EASE }}>
              <ChevronLeftIcon className="shrink-0" aria-hidden />
            </motion.span>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </div>
    </motion.aside>
  );
}
